import { useContext } from 'react';
import { HeaderContext } from '@contexts/HeaderContext';
import { useSiteInfo } from '@contexts/SiteInfoContext';
import SiteMapping from '@configs/siteMapping';
import { MatchSceneStatus, MatchSceneName } from './liveScoreConstants';
import transformLanguage from './transformLanguage';
import { goToSabaScoreSingleMatch } from '../../utils/gotoSabaScoreUtils';

const formatSoccerStatus = (halfStatus, matchTime, homeTeam) => {
    const maxMinutes = halfStatus === '1H' ? 45 : 90;
    if (!matchTime) return halfStatus;
    return (
        <>
            {halfStatus}
            {matchTime > maxMinutes ? (
                <>
                    {maxMinutes}
                    {'+ '}
                    {homeTeam?.InjuryTime ? homeTeam?.InjuryTime : ''}
                </>
            ) : matchTime && (
                <>
                    {matchTime}
                    &#39;
                </>
            )}
        </>
    );
};
const formatLocalDate = (ticks) => {
    if (!ticks) return '';
    const date = new Date(ticks * 1000);
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${mm}/${dd}`;
};
const formatLocalTime = (ticks) => {
    if (!ticks) return '';
    const date = new Date(ticks * 1000);
    const hh = String(date.getHours()).padStart(2, '0');
    const mm = String(date.getMinutes()).padStart(2, '0');
    return `${hh}:${mm}`;
};
const formatBasketballStatus = (statusCode) => {
    let text = '';
    switch (statusCode) {
    case MatchSceneStatus.OneHalf: {
        text = 'Q1';
        break;
    }
    case MatchSceneStatus.OneHalfEnd: {
        text = 'End Q1';
        break;
    }
    case MatchSceneStatus.TwoHalf: {
        text = 'Q2';
        break;
    }
    case MatchSceneStatus.TwoHalfEnd: {
        text = 'End Q2';
        break;
    }
    case MatchSceneStatus.ThreeHalf: {
        text = 'Q3';
        break;
    }
    case MatchSceneStatus.ThreeHalfEnd: {
        text = 'End Q3';
        break;
    }
    case MatchSceneStatus.FourHalf: {
        text = 'Q4';
        break;
    }
    default: {
        text = MatchSceneName[statusCode];
    }
    }
    return text;
};
export const MatchScene = ({
    status,
    matchStatus,
    homeFullScore,
    awayFullScore,
    matchTime,
    schedule,
    scheduleTicks,
    dateShareZh,
    selectedSport,
    matchDate,
    startTime,
    homeTeam,
    awayTeam,
    isResult,
}) => {
    if (selectedSport === 'Basketball') {
        return (
            {
                // Q1 ~ Q4
                [matchStatus]: (
                    <>
                        <div className="text-score">
                            {homeFullScore}
                            {' '}
                            -
                            {' '}
                            {awayFullScore}
                        </div>
                        <div className="text-time">
                            {{
                                [matchStatus]: (<>{formatBasketballStatus(matchStatus)}</>),
                            }[matchStatus]}
                        </div>
                    </>
                ),
                // Deadball
                [MatchSceneStatus.NotStarted]: (
                    <>
                        <span className="text">{schedule.slice(5, 10).replace('-', '/')}</span>
                        <div className="text-time">{schedule.slice(-8, -3)}</div>
                    </>
                ),
                [MatchSceneStatus.Ended]: (
                    <>
                        <span className="text">{dateShareZh?.replace('-', '/')}</span>
                        <div className="text-score">
                            {homeFullScore}
                            {' '}
                            -
                            {' '}
                            {awayFullScore}
                        </div>
                        <div className="text-time">Full Time</div>
                    </>
                ),
                [MatchSceneStatus.Delayed]: (
                    <div className="text-time">
                        {{
                            [matchStatus]: (<>{formatBasketballStatus(matchStatus)}</>),
                        }[matchStatus]}
                    </div>
                ),
                [MatchSceneStatus.ToBeDetermined]: (
                    <>
                        {MatchSceneName[matchStatus]
                            ? <div className="text-time">{MatchSceneName[matchStatus]}</div>
                            : ''}
                    </>
                ),
            }[matchStatus]
        );
    }
    if (selectedSport === 'Cricket') {
        return (
            {
                [MatchSceneStatus.CricketStatus.Other]: <div>Other</div>, // 4
                [MatchSceneStatus.CricketStatus.Completed]: ( // 2
                    <>
                        <span className="text">{matchDate}</span>
                        <div className="text-time">Finished</div>
                    </>
                ),
                [MatchSceneStatus.CricketStatus.Scheduled]: ( // 1
                    <>
                        <span className="text">{matchDate}</span>
                        <div className="text-time">{startTime}</div>
                    </>
                ),
                [MatchSceneStatus.CricketStatus.Live]: <span className="text-live">Live</span>, // 3
            }[status]
        );
    }
    // Soccer
    return (
        {
            // Live, delay
            [status]: (
                <>
                    <div className="text-score">
                        {homeFullScore}
                        {' '}
                        -
                        {' '}
                        {awayFullScore}
                    </div>
                    <div className="text-time">
                        {{
                            [status]: (
                                <>
                                    {MatchSceneName[status]
                                        ? <div className="text-time">{MatchSceneName[status]}</div>
                                        : ''}
                                </>
                            ),
                            [MatchSceneStatus.FirstHalf]: formatSoccerStatus('1H', matchTime, homeTeam),
                            [MatchSceneStatus.Halftime]: 'HT',
                            [MatchSceneStatus.SecondHalf]: formatSoccerStatus('2H', matchTime, homeTeam),
                        }[status]}
                    </div>
                </>
            ),

            // [status]: (<div>{homeTeam.InjuryTime} {awayTeam.InjuryTime}</div>),
            // Deadball
            [MatchSceneStatus.NotStarted]: (
                <>
                    <span className="text">{formatLocalDate(scheduleTicks)}</span>
                    <div className="text-time">{formatLocalTime(scheduleTicks)}</div>
                </>
            ),
            [MatchSceneStatus.Ended]: (
                <>
                    {!isResult ? <span className="text">{dateShareZh?.replace('-', '/')}</span> : ''}
                    <div className="text-score">
                        {homeFullScore}
                        {' '}
                        -
                        {' '}
                        {awayFullScore}
                    </div>
                    <div className="text-time">Full Time</div>
                </>
            ),
        }[status]
    );
};

export const LiveScoreCard = ({
    sportData,
    selectedSport,
    isResult,
}) => {
    const { languageContext } = transformLanguage(sportData);
    const [state] = useContext(HeaderContext);
    const { leagueTeamLogoTimestamp, siteName } = useSiteInfo();
    const withoutSabaScoreLink = [SiteMapping.ocean99, SiteMapping.PICA88];

    return (
        <div
            className={`ls-match${withoutSabaScoreLink.includes(siteName) ? ' ls-match--no-link' : ''}`}
            onClick={withoutSabaScoreLink.includes(siteName) ? undefined : () => goToSabaScoreSingleMatch(
                selectedSport,
                sportData.MatchId,
                state.language,
            )}
        >
            <div className="ls-match__header">
                <span className="text">
                    { languageContext?.league }
                </span>
            </div>
            <div className="ls-match__main">
                <div className="ls-match__team-home">
                    <span className="text">
                        { languageContext?.homeTeam }
                    </span>
                    <div className="team-logo">
                        {
                            sportData.HomeTeam.Icon
                                ? <img src={leagueTeamLogoTimestamp ? `${sportData.HomeTeam.Icon}?v=${leagueTeamLogoTimestamp}` : sportData.HomeTeam.Icon} alt="icon" />
                                : null
                        }
                    </div>
                    {{
                        Cricket: (
                            <>
                                {
                                    (sportData.HomeTeam.Scores !== null) ? (
                                        <span className="text-score">
                                            {sportData.HomeTeam?.Scores}
                                            (
                                            {sportData.HomeTeam?.Overs}
                                            ov)
                                        </span>
                                    ) : ''
                                }
                            </>
                        ),
                    }[selectedSport]}
                </div>
                <div className="ls-match__info">
                    <MatchScene
                        status={sportData.Status}
                        matchStatus={sportData.MatchStatus}
                        homeTeam={sportData.HomeTeam}
                        awayTeam={sportData.AwayTeam}
                        homeFullScore={sportData.HomeTeam?.FullScore}
                        awayFullScore={sportData.AwayTeam?.FullScore}
                        matchTime={sportData.MatchTime}
                        schedule={sportData.Schedule}
                        scheduleTicks={sportData.ScheduleTicks}
                        dateShareZh={sportData?.DateShareZh}
                        matchDate={sportData.MatchDate}
                        startTime={sportData.StartTime}
                        selectedSport={selectedSport}
                        isResult={isResult}
                    />
                </div>
                <div className="ls-match__team-away">
                    <div className="team-logo">
                        {
                            sportData.AwayTeam.Icon
                                ? <img src={leagueTeamLogoTimestamp ? `${sportData.AwayTeam.Icon}?v=${leagueTeamLogoTimestamp}` : sportData.AwayTeam.Icon} alt="icon" />
                                : null
                        }
                    </div>
                    <span className="text">
                        {
                            languageContext?.awayTeam
                        }
                    </span>
                    {{
                        Cricket: (
                            <>
                                {
                                    (sportData.AwayTeam.Scores !== null) ? (
                                        <span className="text-score">
                                            {sportData.AwayTeam?.Scores}
                                            (
                                            {sportData.AwayTeam?.Overs}
                                            ov)
                                        </span>
                                    ) : ''
                                }
                            </>
                        ),
                    }[selectedSport]}
                </div>
            </div>
        </div>
    );
};
